import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { toast } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LazyMotion, domMax, AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/contexts/AuthContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { UpgradeFlowProvider } from "@/contexts/UpgradeFlowContext";
import ImpersonationBar from "@/components/ImpersonationBar";
import ImpersonationBadge from "@/components/ImpersonationBadge";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";
import { useSubscriptionNotifications } from "@/hooks/useSubscriptionNotifications";
import { useRealtimePremiumRequests } from "@/hooks/useRealtimePremiumRequests";
import PreviewInvalidationListener from "@/components/PreviewInvalidationListener";

const SubscriptionNotificationsMount = () => {
  useSubscriptionNotifications();
  useRealtimePremiumRequests();
  return null;
};

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Videos = lazy(() => import("./pages/Videos"));
const DietPlans = lazy(() => import("./pages/DietPlans"));
const DietPlanDetail = lazy(() => import("./pages/DietPlanDetail"));
const Admin = lazy(() => import("./pages/Admin"));
const Appointments = lazy(() => import("./pages/Appointments"));
const Premium = lazy(() => import("./pages/Premium"));
const PremiumRequests = lazy(() => import("./pages/PremiumRequests"));
const Install = lazy(() => import("./pages/Install"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const FoundationProgram = lazy(() => import("./pages/FoundationProgram"));
const StrengthInRange = lazy(() => import("./pages/StrengthInRange"));
const FunctionalResilience = lazy(() => import("./pages/FunctionalResilience"));
const Programs = lazy(() => import("./pages/Programs"));
const ProgramSession = lazy(() => import("./pages/ProgramSession"));
const FitnessAnalysis = lazy(() => import("./pages/FitnessAnalysis"));
const Notifications = lazy(() => import("./pages/Notifications"));
const AccessHistory = lazy(() => import("./pages/AccessHistory"));
const AdminAccessLog = lazy(() => import("./pages/AdminAccessLog"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AIChatbot = lazy(() => import("./components/AIChatbot"));

// Lazy-load non-critical route wrappers
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const AdminRoute = lazy(() => import("./components/AdminRoute"));
const PageTransition = lazy(() => import("./components/PageTransition"));

// Sayfa yenilense bile API verilerinin taze gelmesini garanti eden varsayılanlar:
// - staleTime 0  → her mount'ta yeniden doğrula
// - refetchOnWindowFocus / Reconnect / Mount → kullanıcı sekmeye dönünce veya
//   ağ tekrar gelince otomatik tazele
// - gcTime 5dk   → bellekte gereksiz birikmeyi önle
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

const PageWrap = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="min-h-screen bg-background" />}>
    <PageTransition>{children}</PageTransition>
  </Suspense>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageWrap><Index /></PageWrap>} />
          <Route path="/auth" element={<PageWrap><Auth /></PageWrap>} />
          <Route path="/reset-password" element={<PageWrap><ResetPassword /></PageWrap>} />
          <Route path="/videos" element={<PageWrap><Videos /></PageWrap>} />
          <Route path="/diet-plans" element={<PageWrap><DietPlans /></PageWrap>} />
          <Route path="/diet-plans/:id" element={<PageWrap><DietPlanDetail /></PageWrap>} />
          <Route path="/appointments" element={
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <ProtectedRoute><PageWrap><Appointments /></PageWrap></ProtectedRoute>
            </Suspense>
          } />
          <Route path="/premium" element={<PageWrap><Premium /></PageWrap>} />
          <Route path="/premium/requests" element={
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <ProtectedRoute><PageWrap><PremiumRequests /></PageWrap></ProtectedRoute>
            </Suspense>
          } />
          <Route path="/admin" element={
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <AdminRoute><PageWrap><Admin /></PageWrap></AdminRoute>
            </Suspense>
          } />
          <Route path="/admin/access-log" element={
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <AdminRoute><PageWrap><AdminAccessLog /></PageWrap></AdminRoute>
            </Suspense>
          } />
          <Route path="/dashboard" element={
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <ProtectedRoute><PageWrap><Dashboard /></PageWrap></ProtectedRoute>
            </Suspense>
          } />
          <Route path="/install" element={<PageWrap><Install /></PageWrap>} />
          <Route path="/onboarding" element={<PageWrap><Onboarding /></PageWrap>} />
          <Route path="/programs" element={<PageWrap><Programs /></PageWrap>} />
          <Route path="/program/mobility" element={<PageWrap><FoundationProgram /></PageWrap>} />
          <Route path="/program/strength-in-range" element={<PageWrap><StrengthInRange /></PageWrap>} />
          <Route path="/program/functional-resilience" element={<PageWrap><FunctionalResilience /></PageWrap>} />
          <Route path="/program/:slug/week/:weekNum" element={<PageWrap><ProgramSession /></PageWrap>} />
          <Route path="/program/:slug/week/:weekNum/session/:sessionNum" element={<PageWrap><ProgramSession /></PageWrap>} />
          <Route path="/fitness-analysis" element={
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <ProtectedRoute><PageWrap><FitnessAnalysis /></PageWrap></ProtectedRoute>
            </Suspense>
          } />
          <Route path="/notifications" element={
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <ProtectedRoute><PageWrap><Notifications /></PageWrap></ProtectedRoute>
            </Suspense>
          } />
          <Route path="/access-history" element={
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <ProtectedRoute><PageWrap><AccessHistory /></PageWrap></ProtectedRoute>
            </Suspense>
          } />
          <Route path="*" element={<PageWrap><NotFound /></PageWrap>} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
};

const HtmlLangSync = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language === "tr" ? "tr" : "en";
  }, [i18n.language]);
  return null;
};

// Auto-update flow (silent, no user confirmation):
//  1. `controllerchange` reloads the page exactly once when a freshly activated
//     SW takes control — this swaps in the new build.
//  2. We poll for SW updates on mount, on tab focus, and every 60s.
//  3. As soon as a NEW service worker finishes installing, we IMMEDIATELY post
//     SKIP_WAITING. Combined with `clientsClaim: true` in workbox, this means
//     the new build activates and reloads automatically — users always see the
//     latest published version without having to click anything.
const SWUpdateListener = () => {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const isInIframe = (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })();
    const isPreviewHost =
      window.location.hostname.includes("id-preview--") ||
      window.location.hostname.includes("lovableproject.com");
    if (isInIframe || isPreviewHost) return;

    let refreshing = false;

    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    const promoteWaiting = (reg: ServiceWorkerRegistration) => {
      if (reg.waiting) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    };

    const watchInstalling = (reg: ServiceWorkerRegistration) => {
      const sw = reg.installing;
      if (!sw) return;
      sw.addEventListener("statechange", () => {
        // Only auto-promote when there's already an active controller — i.e.
        // this isn't the very first install on a fresh visit (no reload needed).
        if (sw.state === "installed" && navigator.serviceWorker.controller) {
          promoteWaiting(reg);
        }
      });
    };

    const checkForUpdate = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        // A waiting SW from a previous tab/session — promote immediately.
        if (reg.waiting && navigator.serviceWorker.controller) {
          promoteWaiting(reg);
        }
        if (reg.installing) watchInstalling(reg);
        reg.addEventListener("updatefound", () => watchInstalling(reg));
        await reg.update();
      } catch {
        // Network failures are fine — retry on next visibility/interval.
      }
    };

    checkForUpdate();
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(checkForUpdate, 60_000);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, []);
  return null;
};

// Build version watcher: independent of the service worker. On focus and every
// 60s, fetches /index.html with cache:'no-store' and compares the hashed main
// script URL against the one this tab booted with. If different, a new build
// has been published — clear caches, unregister stale SWs and hard-reload so
// the user always sees the latest deployed version.
const BuildVersionWatcher = () => {
  useEffect(() => {
    // Skip entirely in the Lovable editor iframe / preview hosts. Polling
    // /index.html every 60s there triggers the editor's "Starting live
    // preview…" overlay constantly. Production only.
    const isInIframe = (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })();
    const isPreviewHost =
      window.location.hostname.startsWith("id-preview--") ||
      window.location.hostname.includes("lovableproject.com");
    if (isInIframe || isPreviewHost) return;

    // Capture the initial main script URL from the live document.
    const captureBootSignature = () => {
      const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"));
      const main = scripts.find((s) => /\/assets\/.*\.js/i.test(s.src) || /main\.tsx/i.test(s.src));
      return main?.src || "";
    };
    const bootSignature = captureBootSignature();
    if (!bootSignature) return;

    let reloading = false;
    let promptShown = false;
    // Persistent snooze across reloads/tabs. Bound to the remote build
    // signature so a NEW deploy re-prompts immediately (snooze invalidated).
    const snoozeKey = "build-version-snoozed";
    const dismissKey = "build-version-dismissed";
    const SNOOZE_MS = 4 * 60 * 60 * 1000; // 4 hours

    type SnoozeRecord = { sig: string; until: number };
    const readDismissed = (): string[] => {
      try {
        const raw = localStorage.getItem(dismissKey);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
      } catch {
        return [];
      }
    };
    const writeDismissed = (sig: string) => {
      try {
        const list = readDismissed();
        if (!list.includes(sig)) {
          // Cap to last 20 versions to avoid unbounded growth.
          const next = [...list, sig].slice(-20);
          localStorage.setItem(dismissKey, JSON.stringify(next));
        }
      } catch {
        /* ignore */
      }
    };
    const readSnooze = (): SnoozeRecord | null => {
      try {
        const raw = localStorage.getItem(snoozeKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as SnoozeRecord;
        if (!parsed?.sig || typeof parsed.until !== "number") return null;
        return parsed;
      } catch {
        return null;
      }
    };
    const writeSnooze = (rec: SnoozeRecord) => {
      try {
        localStorage.setItem(snoozeKey, JSON.stringify(rec));
      } catch {
        /* ignore quota / private mode */
      }
    };
    const clearSnooze = () => {
      try {
        localStorage.removeItem(snoozeKey);
      } catch {
        /* ignore */
      }
    };

    const hardReload = async () => {
      if (reloading) return;
      reloading = true;
      clearSnooze();
      try {
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.update().catch(() => null)));
        }
      } catch {
        /* ignore */
      }
      const url = new URL(window.location.href);
      url.searchParams.set("_v", Date.now().toString());
      window.location.replace(url.toString());
    };

    const promptUpdate = async (remoteSignature: string) => {
      if (promptShown || reloading) return;
      // Honor permanent dismissal for this exact build signature.
      if (readDismissed().includes(remoteSignature)) return;
      // Honor snooze: same build + still within snooze window → skip.
      const snooze = readSnooze();
      if (snooze && snooze.sig === remoteSignature && Date.now() < snooze.until) {
        return;
      }
      // Stale snooze for an older build → drop it so the new build prompts.
      if (snooze && snooze.sig !== remoteSignature) clearSnooze();

      promptShown = true;
      const i18n = (await import("./i18n")).default;
      const lang = (i18n.language || (typeof document !== "undefined" && document.documentElement.lang) || "tr").toLowerCase();
      const isTr = lang.startsWith("tr");

      // Fetch changelog notes for the latest build (best-effort; no-store).
      let notes: string[] = [];
      let version = "";
      try {
        const cl = await fetch("/changelog.json", { cache: "no-store" });
        if (cl.ok) {
          const data = await cl.json();
          const latest = data?.entries?.[0];
          if (latest) {
            version = latest.version || "";
            const localized = isTr ? latest.tr : latest.en;
            if (Array.isArray(localized)) notes = localized.slice(0, 5);
          }
        }
      } catch {
        /* changelog optional */
      }

      const fallbackDesc = isTr
        ? "Uygulamanın güncel sürümünü görmek için yenileyin."
        : "Refresh to load the latest version of the app.";

      let toastId: string | number | undefined;
      const dismissForeverLabel = isTr ? "Bu bildirimi gösterme" : "Don't show this again";
      const handleDismissForever = () => {
        writeDismissed(remoteSignature);
        if (toastId !== undefined) toast.dismiss(toastId);
        promptShown = false;
      };

      const description =
        notes.length > 0 ? (
          <div className="text-xs leading-relaxed">
            {version && (
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                v{version}
              </div>
            )}
            <ul className="space-y-1 list-disc pl-4">
              {notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
            <div className="mt-2 text-[10px] text-muted-foreground">
              {isTr ? "“Daha sonra” seçerseniz 4 saat boyunca tekrar sormayacağız." : "Choosing “Later” will snooze this for 4 hours."}
            </div>
            <button
              type="button"
              onClick={handleDismissForever}
              className="mt-1 text-[10px] underline text-muted-foreground hover:text-foreground transition-colors"
            >
              {dismissForeverLabel}
            </button>
          </div>
        ) : (
          <div className="text-xs">
            <div>{fallbackDesc}</div>
            <div className="mt-2 text-[10px] text-muted-foreground">
              {isTr ? "“Daha sonra” seçerseniz 4 saat boyunca tekrar sormayacağız." : "Choosing “Later” will snooze this for 4 hours."}
            </div>
            <button
              type="button"
              onClick={handleDismissForever}
              className="mt-1 text-[10px] underline text-muted-foreground hover:text-foreground transition-colors"
            >
              {dismissForeverLabel}
            </button>
          </div>
        );

      toastId = toast.message(
        isTr ? "Yeni sürüm hazır" : "New version available",
        {
          description,
          duration: Infinity,
          action: {
            label: isTr ? "Şimdi güncelle" : "Update now",
            onClick: () => {
              hardReload();
            },
          },
          cancel: {
            label: isTr ? "Daha sonra" : "Later",
            onClick: () => {
              writeSnooze({ sig: remoteSignature, until: Date.now() + SNOOZE_MS });
              promptShown = false;
            },
          },
        }
      );
    };

    const checkRemote = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const res = await fetch("/index.html", {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!res.ok) return;
        const html = await res.text();
        const match = html.match(/<script[^>]+src=["']([^"']*\/assets\/[^"']+\.js|[^"']*main\.tsx[^"']*)["']/i);
        const remoteMain = match?.[1];
        if (!remoteMain) return;
        const normalize = (u: string) => {
          try {
            return new URL(u, window.location.origin).pathname;
          } catch {
            return u;
          }
        };
        const remoteNorm = normalize(remoteMain);
        if (remoteNorm !== normalize(bootSignature)) {
          promptUpdate(remoteNorm);
        }
      } catch {
        /* network errors are fine */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") checkRemote();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", checkRemote);
    const interval = window.setInterval(checkRemote, 60_000);
    const initial = window.setTimeout(checkRemote, 5_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", checkRemote);
      window.clearInterval(interval);
      window.clearTimeout(initial);
    };
  }, []);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LazyMotion features={domMax}>
      <TooltipProvider>
        <HtmlLangSync />
        <SWUpdateListener />
        <BuildVersionWatcher />
        <PreviewInvalidationListener />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <ImpersonationProvider>
            <AuthProvider>
              <UpgradeFlowProvider>
                <SubscriptionNotificationsMount />
                <AnimatedRoutes />
                <Suspense fallback={null}><AIChatbot /></Suspense>
                <ImpersonationBadge />
                <ImpersonationBar />
              </UpgradeFlowProvider>
            </AuthProvider>
          </ImpersonationProvider>
        </BrowserRouter>
      </TooltipProvider>
    </LazyMotion>
  </QueryClientProvider>
);

export default App;
